import { useCallback, useMemo } from "react";
import { Badge, IndexTable, Button, BlockStack, Text } from "@shopify/polaris";
import { useAppDispatch } from "../../../redux/hooks";
import { setEmulating } from "../../../redux/slices/env";
import type { DbShop } from "@/shared/typings/types/shop";
import { useNavigate } from "react-router-dom";

type Props = {
  shop: DbShop;
  position: number;
};

function formatDate(dateStr: string | undefined | null) {
  if (!dateStr) return "Never";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Never";
    return date.toLocaleDateString();
  } catch {
    return "Never";
  }
}

export default function ShopsRow({ shop, position }: Props) {
  const {
    id,
    shopName,
    shopDomain,
    appPlanName,
    appPlanActive,
    shopCountry,
    shopOwnerEmail,
    appOpenedLast,
    appInstalledAt,
    appInstallCount,
    shopPlanName
  } = shop;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const emulateShop = useCallback(() => {
    dispatch(setEmulating(shopDomain));
    navigate('/');
  }, [dispatch, navigate, shopDomain]);

  const cellValues = useMemo(() => {
    return [
      <BlockStack key="shop">
        <Text as="p" fontWeight="semibold">{shopName}</Text>
        <Text as="p" tone="subdued">{shopDomain}</Text>
      </BlockStack>,
      <Text key="email" as="p">{shopOwnerEmail}</Text>,
      <Text key="country" as="p">{shopCountry}</Text>,
      <Text key="last_seen" as="p">{formatDate(appOpenedLast)}</Text>,
      <Text key="installed_at" as="p">{formatDate(appInstalledAt)}</Text>,
      <Text key="installs" as="p">{appInstallCount}</Text>,
      <Text key="shopify_plan" as="p">{shopPlanName}</Text>,
      <Badge
        key="app_plan"
        tone={appPlanActive ? 'success' : undefined}
        progress={appPlanActive ? 'complete' : 'incomplete'}
      >
        {appPlanName || 'No plan'}
      </Badge>,
      <Button key="emulate" onClick={emulateShop}>Emulate</Button>
    ];
  }, [appInstallCount, appInstalledAt, appPlanActive, appPlanName, emulateShop, appOpenedLast, shopCountry, shopDomain, shopName, shopOwnerEmail, shopPlanName]);

  return (
    <IndexTable.Row position={position} id={id}>
      {cellValues.map((value, index) => (
        <IndexTable.Cell key={index}>
          {value}
        </IndexTable.Cell>
      ))}
    </IndexTable.Row>
  );
}
